import { useTranslation } from '@shared/core/i18n';
import { useMemo } from 'react';
import { useMarcomAppTelemetry } from '../MarcomApp.telemetry';
import type { FooterLink, FooterLinksType, RenderLinkProps } from './AppFooter.types';
import { withWindow } from '@shared/utils/withWindow';
import { useShopStorefrontContext } from '@apps/registry/common/components/Catalog/components/ShopStorefrontProvider';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';

const FOOTER_LINKS: FooterLink = {
  wedding: {
    primary: [
      { id: 'footer-wedding-website', label: ({ primary }) => primary.weddingWebsite, href: '/wedding-website', target: '_blank' },
      { id: 'footer-guest-list', label: ({ primary }) => primary.guestList, href: '/guest-list', target: '_blank' },
      { id: 'footer-save-the-date', label: ({ primary }) => primary.saveTheDates, href: '/cards/wedding/save-the-date', target: '_blank' },
      { id: 'footer-wedding-invitations', label: ({ primary }) => primary.invitations, href: '/wedding-invitations', target: '_blank' },
      { id: 'footer-smart-rsvp', label: ({ primary }) => primary.smartRsvp, href: '/online-rsvp', target: '_blank' },
      { id: 'footer-registry', label: ({ primary }) => primary.registry, href: '/registry', target: '_blank' },
      { id: 'footer-hotel-room-blocks', label: ({ primary }) => primary.hotelRoomBlocks, href: '/hotel-room-blocks', target: '_blank' },
      { id: 'footer-app', label: ({ primary }) => primary.mobileApp, href: '/app', target: '_blank' },
    ],
    secondary: [
      { id: 'footer-designTemplates', label: ({ secondary }) => secondary.designTemplates, href: '/designs', target: '_blank' },
      { id: 'footer-ideasInspiration', label: ({ secondary }) => secondary.ideasInspiration, href: 'https://withjoy.com/blog/', target: '_blank' },
      { id: 'footer-commonQuestions', label: ({ secondary }) => secondary.commonQuestions, href: '/faq', target: '_blank' },
      { id: 'footer-helpSupport', label: ({ secondary }) => secondary.helpSupport, href: 'https://help.withjoy.com/', target: '_blank' },
      { id: 'footer-pricing', label: ({ secondary }) => secondary.pricing, href: '/pricing', target: '_blank' },
    ],
  },
  baby: {
    primary: [
      { id: 'footer-baby-registry', label: ({ primary }) => primary.babyRegistry, href: '/baby-registry', target: '_blank' },
      { id: 'footer-baby-shop-gifts', label: ({ primary }) => primary.shopBabyGifts, href: '/shop/baby', target: '_blank' },
      { id: 'footer-baby-zero-fee-cash-funds', label: ({ primary }) => primary.zeroFeeCashFunds, href: '/shop/baby/catalog/baby-cash-funds', target: '_blank' },
    ],
    secondary: [
      {
        id: 'footer-baby-blog',
        href: 'https://withjoy.com/baby-guides/',
        target: '_blank',
        label: ({ secondary }) => secondary.babyParentingGuides,
      },
      { id: 'footer-baby-help', href: 'https://help.withjoy.com/baby-registry/', label: ({ secondary }) => secondary.helpSupport, target: '_blank' },
    ],
  },
};

export const useFooterLinks = () => {
  const { t2 } = useTranslation('marcom');
  const { wwwNavigate } = useMarcomAppTelemetry();
  const { currentStorefront } = useShopStorefrontContext();

  const getLinkProps = useMemo(
    () =>
      (id: string, href: string, nodeText: string, target?: string): RenderLinkProps => {
        return {
          id,
          href,
          target,
          onClick: () => {
            wwwNavigate({ elementId: id, to: href, nodeText });
          },
          onKeyDown: (e: KeyboardEvent) => {
            if (e.key === 'Enter') {
              wwwNavigate({ elementId: id, to: href, nodeText });
              if (href) {
                withWindow(global => {
                  global.document.location.href = href;
                });
              }
            }
          },
          label: nodeText,
        };
      },
    [wwwNavigate],
  );

  const footerLinks = useMemo(() => {
    return FOOTER_LINKS[currentStorefront];
  }, [currentStorefront]);

  const itemsToRender = useEventCallback(
    (type: FooterLinksType) => {
      const primary = t2('footerMenu', 'primary');
      const secondary = t2('footerMenu', 'secondary');

      const preparedLinks: Array<RenderLinkProps> = [];

      const FooterLinks = type ? FOOTER_LINKS[currentStorefront][type] : [];

      FooterLinks.forEach(({ id, label, href, target }) => {
        const nodeText = label({ primary, secondary });
        preparedLinks.push({ ...getLinkProps(id, href, nodeText, target) });
      });

      return preparedLinks;
    },
    [t2, getLinkProps, currentStorefront],
  );

  return {
    footerLinks,
    itemsToRender,
  } as const;
};
